import _, { get, attempt, identity } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  AlignLeftButton,
  AlignRightButton,
  SizeFullWidthButton,
  SizeContentButton,
  SizeTextButton,
  SizeOriginalButton,
  ReuploadButton,
  ReuploadMediaDialogButton,
  ChangeButton,
  LinkButton,
  DeleteButton,
} from '../../buttons';
import classNames from 'classnames';
import { findDOMNode } from 'react-dom';
import styles from './media-block-editor.scss';
import { VerticalSeparator } from '../../../separator';
import ToolbarInput from '../../toolbar-input';
import getToolbarPosition from '../../utils/get-toolbar-position';
import { isEditor } from '../../../../selectors/view-mode-selectors';

class MediaBlockEditor extends Component {
  state = {};

  componentDidMount() {
    this.props.pluginData.subscribeToItem('visibleBlock', this.onVisibilityChanged);
    this.props.pluginData.subscribeToItem('alignment', this.setAlignment);
    this.props.pluginData.subscribeToItem('size', this.setSize);
  }

  componentWillUnmount() {
    this.clearTimeout();
  }

  clearTimeout = () => clearTimeout(this.timeout);

  onVisibilityChanged = visibleBlock => {
    this.clearTimeout();
    // timeout is needed here to get correct position
    this.timeout = setTimeout(() => this.updateState(visibleBlock));
  };

  updateState = visibleBlock => {
    const toolbar = findDOMNode(this);
    const style = visibleBlock ?
      getToolbarPosition(
        toolbar.parentNode.getBoundingClientRect(),
        this.props.pluginData.getItem('boundingRect'),
        toolbar.getBoundingClientRect()
      ) :
      { visibility: 'hidden' };

    const alignment = this.props.pluginData.getItem('alignment') || 'default';
    this.setState({
      isVisible: Boolean(visibleBlock),
      alignment,
      style,
      isInputVisible: false,
    });
  };

  handleUpload = event =>
    this.props.onUpload({
      file: event.target.files[0],
      entityKey: this.props.pluginData.getItem('entityKey'),
    });

  setAlignment = alignment => this.setState({ alignment });
  setSize = size => this.setState({ size });

  isUploading = () => this.props.pluginData.getItem('entity').data.isLoading;

  handleDeleteButtonClick = () => {
    const { pluginData } = this.props;
    pluginData.updateItem('visibleBlock', null);
    pluginData.getItem('deleteBlock')();
  };

  handleLinkFormSubmit = href => {
    const { pluginData } = this.props;
    const setHref = pluginData.getItem('setHref');

    this.hideInput();
    setHref(href);
  };

  handleLinkButtonClick = () =>
    this.setState({
      isInputVisible: true,
      initialInputValue: get(this.props.pluginData.getItem('entity'), 'data.href'),
      submitHandler: this.handleLinkFormSubmit,
    });

  handleChangeVideoButtonClick = () =>
    this.setState({
      isInputVisible: true,
      initialInputValue: get(this.props.pluginData.getItem('entity'), 'data.url'),
      submitHandler: this.handleVideoEmbedFormSubmit,
    });

  handleVideoEmbedFormSubmit = url =>
    this.props.onVideoEmbedChange({
      entityKey: this.props.pluginData.getItem('entityKey'),
      url,
    });

  handleCancel = () =>
    this.setState({
      isInputVisible: false,
      initialInputValue: null,
      submitHandler: null,
    });

  handleMouseDown = event => event.preventDefault();

  renderInput = () => {
    const { isInputVisible, initialInputValue, submitHandler } = this.state;
    return isInputVisible ? (
      <div className={styles.input}>
        <ToolbarInput onSubmit={submitHandler} onCancel={this.handleCancel} initialValue={initialInputValue}/>
      </div>
    ) : null;
  };

  renderButton = (buttonType, key) => {
    const { alignment, size } = this.state;
    const { pluginData, theme, isEditor, onFileSelect } = this.props;
    const setSize = pluginData.getItem('setSize');
    const setAlignment = pluginData.getItem('setAlignment');
    const buttonTheme = get(theme, 'button');

    switch (buttonType) {
      case 'sizeFullWidth':
        return wrapButton(<SizeFullWidthButton setSize={setSize} size={size} theme={buttonTheme}/>, key);
      case 'sizeContent':
        return wrapButton(
          <SizeContentButton setSize={setSize} size={size} theme={buttonTheme}/>,
          key,
          styles.smallSpace
        );
      case 'sizeText':
        return wrapButton(<SizeTextButton setSize={setSize} size={size} theme={buttonTheme}/>, key, styles.smallSpace);
      case 'sizeOriginal':
        return wrapButton(<SizeOriginalButton setSize={setSize} size={size} theme={buttonTheme}/>, key);

      case 'alignLeft':
        return wrapButton(
          <AlignLeftButton setAlignment={setAlignment} alignment={alignment} theme={buttonTheme}/>,
          key
        );
      case 'alignRight':
        return wrapButton(
          <AlignRightButton setAlignment={setAlignment} alignment={alignment} theme={buttonTheme}/>,
          key
        );

      case 'reupload':
        if (isEditor) {
          return wrapButton(
            <ReuploadMediaDialogButton
              onFileSelect={onFileSelect}
              isDisabled={this.isUploading()}
              theme={buttonTheme}
            />,
            key
          );
        }

        return wrapButton(
          <ReuploadButton onFileChange={this.handleUpload} isDisabled={this.isUploading()} theme={buttonTheme}/>,
          key
        );

      case 'changeVideo':
        return wrapButton(<ChangeButton onClick={this.handleChangeVideoButtonClick} theme={buttonTheme}/>, key);

      case 'link':
        return wrapButton(<LinkButton onClick={this.handleLinkButtonClick} theme={buttonTheme}/>, key);

      case 'delete':
        return wrapButton(<DeleteButton onClick={this.handleDeleteButtonClick} theme={buttonTheme}/>, key);

      case '-':
        return (
          <div className={styles.separator} key={key}>
            <VerticalSeparator/>
          </div>
        );

      default:
        return null;
    }
  };

  render() {
    const { pluginData, theme } = this.props;
    const { isVisible, style } = this.state;
    const containerClassName = classNames(styles.container, get(theme, 'container'), 'media-block-editor', {
      [styles.visible]: pluginData.getItem('editorFocus') && isVisible,
    });
    const containerStyle = {
      ...style,
      ...get(theme, 'containerStyle'),
    };
    const entity = pluginData.getItem('entity');
    const buttons = attempt(this.props.buttons, entity);
    return (
      <div
        className={containerClassName}
        style={containerStyle}
        onClick={() => {}} // needed to receive mouse events on iOS
        onMouseDown={this.handleMouseDown}
      >
        {this.renderInput()}
        <div className={styles.flexContainer}>
          {_(buttons)
            .filter(identity)
            .map((button, index) => this.renderButton(button, index))
            .value()}
        </div>
      </div>
    );
  }
}

function wrapButton(button, key, className) {
  return (
    <div className={classNames(styles.button, className)} key={key}>
      {button}
    </div>
  );
}

MediaBlockEditor.propTypes = {
  pluginData: PropTypes.object,
  cardBackgroundColor: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  onVideoEmbedChange: PropTypes.func.isRequired,
  buttons: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  theme: PropTypes.object,
  isEditor: PropTypes.bool,
};

const mapStateToProps = state => ({
  isEditor: isEditor(state),
});

export default connect(mapStateToProps)(MediaBlockEditor);



// WEBPACK FOOTER //
// ./app/components/text-editor/plugins/media-block/media-block-editor.js